/**
 * useAMNotifications — Realtime AM notification hook
 *
 * Fetches notifications for the current AM, subscribes to realtime inserts,
 * and provides mark-as-read + mark-all-read actions.
 */

import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AMNotificationType =
  | "task_auto_created"
  | "sla_warning"
  | "sla_breach"
  | "health_alert"
  | "contract_expiring"
  | "new_message";

export interface AMNotification {
  id: string;
  amId: string;
  type: AMNotificationType;
  title: string;
  body: string | null;
  metadata: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

function mapRow(row: any): AMNotification {
  return {
    id: row.id,
    amId: row.am_id,
    type: row.type as AMNotificationType,
    title: row.title,
    body: row.body,
    metadata: row.metadata as Record<string, unknown> | null,
    readAt: row.read_at,
    createdAt: row.created_at,
  };
}

export function useAMNotifications(amId: string | null) {
  const [notifications, setNotifications] = useState<AMNotification[]>([]);
  const [loading, setLoading] = useState(true);

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const fetchNotifications = useCallback(async () => {
    if (!amId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("am_notifications")
      .select("*")
      .eq("am_id", amId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("[useAMNotifications] Fetch error:", error);
    } else {
      setNotifications((data || []).map(mapRow));
    }
    setLoading(false);
  }, [amId]);

  useEffect(() => {
    fetchNotifications();

    if (!amId) return;

    const channel = supabase
      .channel(`am_notifications_${amId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "am_notifications", filter: `am_id=eq.${amId}` },
        (payload) => {
          setNotifications((prev) => [mapRow(payload.new), ...prev].slice(0, 50));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [amId, fetchNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    const { error } = await supabase
      .from("am_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!amId) return;

    const unreadIds = notifications.filter((n) => !n.readAt).map((n) => n.id);
    if (unreadIds.length === 0) return;

    const { error } = await supabase
      .from("am_notifications")
      .update({ read_at: new Date().toISOString() })
      .in("id", unreadIds);

    if (!error) {
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.readAt ? n : { ...n, readAt: now }))
      );
    }
  }, [amId, notifications]);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead };
}
